import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Portal, Stack, Typography } from '@mui/material';
import { HourglassEmpty, TransitEnterexit } from '@mui/icons-material';
import { useDrawingArea, useSvgRef, useXScale } from '@mui/x-charts-pro';
import { formatMsUTCToHM, timeDurationShort } from '@/utilities/timeUtils';
import { TachographState } from './TachographService';
import { colorFromState, isInvertibleScale } from './TachographDiagramTypes';

export function CustomChartItemTooltip({
  times,
  states,
  isPhoneView,
}: {
  times?: number[];
  states: TachographState[];
  isPhoneView: boolean;
}) {
  const { t } = useTranslation();
  const svgRef = useSvgRef();
  const scale = useXScale();
  const { left, width, top, height } = useDrawingArea();
  const [startIndex, setStartIndex] = useState(-1);
  const [tooltip, setTooltip] = useState<{ x: number; y: number } | null>(null);
  const tooltipWidth = 90;
  const offsetX = 10;
  const offsetY = isPhoneView ? 100 : 0;

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onLeave = () => setStartIndex(-1);

    function toSvg(e: PointerEvent): { x: number; y: number } | null {
      if (!el) return null;

      const pt = el.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const m = el.getScreenCTM();

      if (!m) return null;
      const x = pt.matrixTransform(m.inverse()).x;
      const y = pt.matrixTransform(m.inverse()).y;
      return { x, y };
    }

    const onMove = (e: PointerEvent) => {
      const p = toSvg(e);
      if (!p || p.x == null || p.y == null || p.x < left || p.x > left + width || p.y < top || p.y > top + height) {
        setStartIndex(-1);
        setTooltip(null);
        return;
      }
      if (isInvertibleScale(scale)) {
        if (!times || times.length < 2) {
          setStartIndex(-1);
          setTooltip(null);
          return;
        }

        function toNum(v: number | Date): number {
          return v instanceof Date ? v.getTime() : v;
        }

        function findSegmentIndex(edgesNum: number[], valNum: number): number {
          let lo = 0;
          let hi = edgesNum.length - 1;
          while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (edgesNum[mid] <= valNum) lo = mid + 1;
            else hi = mid;
          }
          return Math.max(0, Math.min(lo - 1, edgesNum.length - 2));
        }
        const valNum = toNum(scale.invert(p.x));
        const v = Math.round(valNum * 3_600_000);
        const edgesNum = times.map(toNum);
        const i = findSegmentIndex(edgesNum, v);
        setStartIndex(i);
        if (p.x + tooltipWidth > left + width) {
          setTooltip({ x: e.clientX - tooltipWidth - offsetX, y: e.clientY - offsetY });
        } else {
          setTooltip({ x: e.clientX + offsetX, y: e.clientY - offsetY });
        }
        return;
      }
      setStartIndex(-1);
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('pointercancel', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('pointercancel', onLeave);
    };
  }, [times, svgRef, left, width, top, height, scale]);

  if (startIndex === -1 || !times || !tooltip) return;
  return (
    <Portal>
      <Box
        sx={{ position: 'fixed', zIndex: 150000, pointerEvents: 'none' }}
        style={{ left: tooltip.x, top: tooltip.y }}
      >
        <Box
          bgcolor="white"
          borderRadius={2}
          border={2}
          borderColor={colorFromState[states[startIndex]]}
          width={tooltipWidth}
          p={0.25}
        >
          <Stack direction="row" color="secondary.main" bgcolor="grey.100">
            <TransitEnterexit fontSize="small" sx={{ transform: 'rotate(270deg)', mx: 0.5 }} />
            <Typography fontSize={13} fontWeight="bold">
              {formatMsUTCToHM(times[startIndex])}
            </Typography>
          </Stack>
          <Stack direction="row">
            <HourglassEmpty fontSize="small" sx={{ mx: 0.5 }} />
            <Typography fontSize={13} fontWeight="bold">
              {timeDurationShort(t, (times[startIndex + 1] - times[startIndex]) * 1000, undefined, true)}
            </Typography>
          </Stack>
          <Stack direction="row" color="primary.main" bgcolor="grey.100">
            <TransitEnterexit fontSize="small" sx={{ mx: 0.5 }} />
            <Typography fontSize={13} fontWeight="bold">
              {formatMsUTCToHM(times[startIndex + 1])}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Portal>
  );
}
